/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useContext, useMemo, useState } from "react";
import useRefValue from "../hooks/useRefValue";
import { FormContext } from "./FormProvider";
function nestedKey(state, key) {
    const p = key.split(".");
    if (p.length === 1)
        return state[key];
    let v = state[p[0]];
    for (let i = 1; i < p.length; ++i) {
        if (v === undefined)
            return undefined;
        if (Array.isArray(v)) {
            const idx = parseInt(p[i]);
            if (isNaN(idx))
                throw new Error(`Invalid key, index is not a number at ${p.slice(0, i + 1).join(".")}`);
            if (v.length < idx)
                return undefined;
            v = v[idx];
        }
        else {
            v = v[p[i]];
        }
    }
    return v;
}
function nestedUpdate(state, key, value, path) {
    if (!key)
        return value;
    const p = key.split(".");
    if (state === undefined) {
        const idx = parseInt(p[0]);
        if (!isNaN(idx) && idx.toString() === p[0]) {
            const s = [];
            s[idx] = nestedUpdate(undefined, p.slice(1).join("."), value, (path ? path + "." : "") + p[0]);
            return s;
        }
        else {
            return {
                [p[0]]: nestedUpdate(state[p[0]], p.slice(1).join("."), value, (path ? path + "." : "") + p[0]),
            };
        }
    }
    if (Array.isArray(state)) {
        const idx = parseInt(p[0]);
        if (isNaN(idx))
            throw new Error(`Invalid key, index is not a number at ${path}[${p[0]}]`);
        return [
            ...state.slice(0, idx),
            nestedUpdate(state[idx], p.slice(1).join("."), value, (path ? path + "." : "") + p[0]),
            ...state.slice(idx + 1),
        ];
    }
    return {
        ...state,
        [p[0]]: nestedUpdate(state[p[0]], p.slice(1).join("."), value, (path ? path + "." : "") + p[0]),
    };
}
/**
 * Provides the form state manager and corresponding utilities.
 */
function useForm(config) {
    const { defaultValues, onSubmit, onErrors, langSet } = config || {};
    const [state, setState] = useState(defaultValues || {});
    const [errors, setErrors] = useState({});
    const [isDirty, setDirty] = useState(false);
    const [dirtyError, setDirtyError] = useState(false);
    const stateRef = useRefValue(state);
    const errorsRef = useRefValue(errors);
    const isDirtyRef = useRefValue(isDirty);
    const validate = useCallback(() => {
        return null;
    }, []);
    const handleSubmit = useCallback((e) => {
        e === null || e === void 0 ? void 0 : e.preventDefault();
        const errors = validate();
        if (errors) {
            setErrors(errors || {});
            onErrors && onErrors(errors);
            return;
        }
        onSubmit &&
            onSubmit(stateRef.current, (success) => {
                if (success)
                    setDirty(false);
            });
    }, [validate, onSubmit, onErrors]);
    const form = useMemo(() => ({
        getState: () => stateRef.current,
        getValue: (key) => nestedKey(stateRef.current, key),
        getError: (key) => errorsRef.current[key],
        isDirty: () => isDirtyRef.current,
        setValue: (key, value) => {
            setDirty(true);
            setState((state) => nestedUpdate(state, key, value));
        },
        append: (arrayKey, value) => {
            setDirty(true);
            setState((state) => {
                const array = nestedKey(state, arrayKey);
                if (!Array.isArray(array))
                    throw new Error(`${arrayKey} is not an array key`);
                return nestedUpdate(state, arrayKey, [...array, value]);
            });
        },
        insert: (arrayKey, idx, value) => {
            setDirty(true);
            setState((state) => {
                const array = nestedKey(state, arrayKey);
                if (!Array.isArray(array))
                    throw new Error(`${arrayKey} is not an array key`);
                return nestedUpdate(state, arrayKey, [
                    ...array.slice(0, idx),
                    value,
                    ...array.slice(idx),
                ]);
            });
        },
        remove: (arrayKey, idx) => {
            setDirty(true);
            setState((state) => {
                const array = nestedKey(state, arrayKey);
                if (!Array.isArray(array))
                    throw new Error(`${arrayKey} is not an array key`);
                return nestedUpdate(state, arrayKey, array.filter((_, i) => idx !== i));
            });
        },
        validate,
        submit: handleSubmit,
        langSet: langSet || "Form",
    }), [handleSubmit, validate, langSet]);
    return {
        form,
        state,
        isDirty,
        setDirty,
        dirtyError,
        setDirtyError,
    };
}
/**
 * Get the form API from the current form context.
 *
 * @returns The form API object
 */
export function useFormApi() {
    return useContext(FormContext).form;
}
export default useForm;
